import { useState, useRef, useEffect, useMemo } from "react";
import { useUser } from "../../../contexts/userContext/userContext.js";
import { useModal } from "../../../contexts/modalContext/modalContext.js";
import { useContent } from "../../../contexts/contentContext/contentContext.js";
import { useToasts } from "react-toast-notifications";
import Button from "../../Elements/Button/Button.js";
import LoaderContainer from "../LoaderContainer/LoaderContainer.js";
import Icon from "../../Elements/Icon/Icon.tsx";
import Toast from "../../Elements/Toast/Toast.js";
import BrandedCoverSelector from "./BrandedCoverSelector.js";
import SpecificMailBrandedCoverSelector from "./SpecificMailBrandedCoverSelector.js";
import { SizeMe } from "react-sizeme";
import { useMail } from "../../../contexts/mailContext/mailContext.js";
import config from "config.ts";

import "./BrandedCoverUploader.scss";

const backgroundImage = `${config.api.resource}/assets/images/upload-cover-background.png`;

const fileReader = new FileReader();

const BrandedCoverUploader = ({ setDefault, setPerMail, selectedFileId }) => {
  //pro users: allow uploading generic cover.
  //business users: first allow uploading generic cover, than allow uploading cover per mail
  const { isBusiness, isPremium } = useUser();
  const {
    getBackgroundCover,
    setDefaultBackgroundCover,
    setSpecificMailBrandedCover,
    getFilesData,
    getMailStatus,
    getImageSrcForCanvas,
    getFilesList,
  } = useMail();

  const isDefaultHelper = isBusiness() || isPremium() ? false : true;

  if (getMailStatus() !== "sent") {
    setDefault = true;
  }

  //logic for submit button - if father-component set one of the setDefault/setPerMail boolean as true, it will overwrite user logic.

  const submit = setDefault
    ? setDefaultBackgroundCover
    : setPerMail
    ? setSpecificMailBrandedCover
    : isDefaultHelper
    ? setDefaultBackgroundCover
    : setSpecificMailBrandedCover;

  //canvas logic
  const { closeModal } = useModal();
  const [loading, setLoading] = useState(false);
  const { getTextByKey } = useContent();
  const { addToast } = useToasts();
  const [image, setImage] = useState(null);
  const [isImageFromPC, setIsImageFromPC] = useState(false);
  const [error, setError] = useState();
  const [fileId, setFileId] = useState(selectedFileId || 0);
  const inputRef = useRef();
  const canvasRef = useRef();
  const uploadedFileName = useRef();

  fileReader.onload = function (event) {
    var image = new Image();
    image.onload = function () {
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      const ratio = image.width > 2500 ? image.width / 2500 : 1;
      canvas.width = image.width / ratio;
      canvas.height = image.height / ratio;
      context.drawImage(
        image,
        0,
        0,
        image.width,
        image.height,
        0,
        0,
        canvas.width,
        canvas.height
      );
      setImage(canvas.toDataURL());
    };
    image.src = event.target.result;
  };

  const getImageSrc = async (fileId) => {
    const res = await getImageSrcForCanvas({ fileId: fileId });
    if (res === "wrong-ratio") {
      setImage("error");
    } else {
      setImage(res);
    }
  };

  //side-effect for selecting image from mail
  useEffect(() => {
    if (fileId) {
      getImageSrc(fileId);
      // setImage(getFilesData()[fileId].PreviewSrc);
      setIsImageFromPC(false);
    }
  }, [fileId]);

  //function will be fired after selecting photo from pc
  const handleNewImage = (e) => {
    if (e.target.files[0]) {
      setFileId(0);
      setIsImageFromPC(true);
      fileReader.readAsDataURL(e.target.files[0]);
      uploadedFileName.current = e.target.files[0].name.replaceAll("&", "-");
    }
  };

  //clicking to "upload" button
  const handleClick = () => {
    inputRef.current.click();
  };

  // get the list of images within current email
  const mailImagesList = useMemo(
    () =>
      getFilesList().filter(
        (fileId) => getFilesData()[fileId]?.Type === "Image"
      ),
    [getFilesList()]
  );

  // set first image from the list as cover

  useEffect(() => {
    mailImagesList.length > 0 && !image && !getBackgroundCover()
      ? getImageSrc(mailImagesList[0])
      : (() => {})();
  }, [mailImagesList]);

  // checking a size of selected image to
  const [imageSizes, setImageSizes] = useState({
    valid: true,
    width: "",
    height: "",
  });

  //saving image to s3 storage
  const handleSubmit = async () => {
    let response;
    setLoading(true);
    const canvas = canvasRef.current.getImage().toDataURL();
    // const canvas = isImageFromPC
    //   ? canvasRef.current.getImage().toDataURL()
    //   : canvasRef.current.props.image;

    var proxyUrl =
      canvas.indexOf("data:image/png;base64,") > -1 || isImageFromPC
        ? ""
        : "https://proxy.jumbomail.me/";

    const blobResponse = await fetch(proxyUrl + canvas)
      .then((res) => res.blob())
      .then((blob) => {
        return blob;
      });

    const name =
      image && image.name
        ? image.name.replaceAll("&", "-")
        : fileId
        ? getFilesData()[fileId].Name.replaceAll("&", "-")
        : selectedFileId
        ? getFilesData()[selectedFileId].Name.replaceAll("&", "-")
        : uploadedFileName.current;

    response = await submit(blobResponse, name, fileId);

    if (response.status === "success") {
      closeModal();
      addToast(getTextByKey("upload-branded-cover-success"), {
        appearance: "success",
        autoDismissTimeout: 6000,
        autoDismiss: true,
      });
    } else {
      setError(response.data);
    }
    setLoading(false);
    setFileId(0);
  };

  return (
    <div className="branded-cover-container">
      <input
        accept="image/*"
        className="click-to-action-upload-input"
        name="newImage"
        type="file"
        onChange={handleNewImage}
        ref={inputRef}
      ></input>
      <div className="branded-cover-title title">
        {getTextByKey("specific-mail-cover-title")}
        <div className="branded-cover-sub-title">
          {getTextByKey("specific-mail-cover-description")}
        </div>
      </div>
      <div className="branded-cover-content">
        <SizeMe>
          {({ size }) => (
            <div className="branded-cover-content-start">
              <BrandedCoverSelector
                backgroundImage={backgroundImage}
                image={image}
                size={size}
                handleClick={handleClick}
                handleNewImage={handleNewImage}
                inputRef={inputRef}
                ref={canvasRef}
                setImageSizes={setImageSizes}
                imageSizes={imageSizes}
              />
              <div className={`branded-cover-footer`}>
                {imageSizes.valid ? (
                  <div className="branded-cover-submit-button">
                    <LoaderContainer isLoading={loading}>
                      <Button
                        className={image ? null : "hidden"}
                        onClick={handleSubmit}
                      >
                        {getTextByKey("branded-cover-update-image")}
                      </Button>
                    </LoaderContainer>
                  </div>
                ) : null}
                <Toast
                  isShow={error}
                  set={setError}
                  error
                  withPadding
                  disableNewLine
                >
                  {error}
                </Toast>
              </div>
            </div>
          )}
        </SizeMe>
        <div className="branded-cover-content-end">
          <SpecificMailBrandedCoverSelector
            {...{ setFileId, handleClick, mailImagesList }}
          />
          <div className="branded-cover-content-footer">
            <Icon name="image" gray />
            <span>{getTextByKey("cover-image-file-limitations")}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandedCoverUploader;
